import type { InputManager, ActionName } from './InputManager';
import type { Settings } from '../core/Settings';

/**
 * Mobil dokunmatik kontroller.
 * Her parmak (touch identifier) bağımsız izlenir:
 *  - sol yarıdaki parmak -> joystick
 *  - sağ yarıdaki serbest parmak -> kamera
 *  - butonlar -> kendi parmaklarıyla, hareketi/kamerayı kesmeden
 * Butonlar birbirine yakın olmayacak şekilde ve ayarlanabilir ölçekte yerleşir.
 */

interface ButtonSpec {
  action: ActionName;
  label: string;
  cls: string;
  hold: boolean;
}

const BUTTONS: ButtonSpec[] = [
  { action: 'attack', label: '⚔️', cls: 'tc-attack', hold: true },
  { action: 'jump', label: '⤒', cls: 'tc-jump', hold: true },
  { action: 'interact', label: '✋', cls: 'tc-interact', hold: false },
  { action: 'sprint', label: '»', cls: 'tc-sprint', hold: true },
  { action: 'crouch', label: '⤓', cls: 'tc-crouch', hold: true },
  { action: 'power', label: '1', cls: 'tc-power', hold: false },
];

export class TouchControls {
  readonly root: HTMLDivElement;
  private stickBase: HTMLDivElement;
  private stickKnob: HTMLDivElement;
  private stickTouch: number | null = null;
  private stickOrigin = { x: 0, y: 0 };
  private lookTouch: number | null = null;
  private lookLast = { x: 0, y: 0 };
  private buttonTouches = new Map<number, ActionName>();
  private visible = false;
  private readonly stickRadius = 58;

  constructor(private input: InputManager, private settings: Settings) {
    this.root = document.createElement('div');
    this.root.className = 'touch-controls';
    this.root.style.display = 'none';

    this.stickBase = document.createElement('div');
    this.stickBase.className = 'tc-stick-base';
    this.stickKnob = document.createElement('div');
    this.stickKnob.className = 'tc-stick-knob';
    this.stickBase.appendChild(this.stickKnob);
    this.root.appendChild(this.stickBase);

    for (const b of BUTTONS) {
      const el = document.createElement('button');
      el.className = `tc-btn ${b.cls}`;
      el.type = 'button';
      el.textContent = b.label;
      el.dataset.action = b.action;
      el.setAttribute('aria-label', b.action);
      this.root.appendChild(el);
    }

    this.applyLayout();
    this.bind();
    this.settings.events.on('controls:changed', () => this.applyLayout());
  }

  setVisible(on: boolean): void {
    this.visible = on;
    this.root.style.display = on ? 'block' : 'none';
    if (!on) this.releaseAll();
  }

  private applyLayout(): void {
    const s = this.settings.controls.buttonScale;
    this.root.style.setProperty('--tc-scale', String(s));
    this.root.classList.toggle('tc-left-handed', this.settings.controls.layout === 'left');
  }

  private releaseAll(): void {
    this.input.setStick(0, 0);
    this.stickKnob.style.transform = 'translate(-50%, -50%)';
    for (const action of this.buttonTouches.values()) this.input.setTouchAction(action, false);
    this.buttonTouches.clear();
    this.stickTouch = null;
    this.lookTouch = null;
  }

  private bind(): void {
    const onStart = (e: TouchEvent) => {
      if (!this.visible) return;
      for (const t of Array.from(e.changedTouches)) {
        const target = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
        const btn = target?.closest('.tc-btn') as HTMLElement | null;
        if (btn?.dataset.action) {
          e.preventDefault();
          const action = btn.dataset.action as ActionName;
          this.buttonTouches.set(t.identifier, action);
          this.input.setTouchAction(action, true);
          btn.classList.add('tc-active');
          continue;
        }
        // Joystick alanı: sol yarı (sol el düzeninde sağ yarı)
        const leftHalf = t.clientX < window.innerWidth / 2;
        const stickSide = this.settings.controls.layout === 'left' ? !leftHalf : leftHalf;
        if (stickSide && this.stickTouch === null) {
          e.preventDefault();
          this.stickTouch = t.identifier;
          this.stickOrigin = { x: t.clientX, y: t.clientY };
          this.stickBase.style.left = `${t.clientX}px`;
          this.stickBase.style.top = `${t.clientY}px`;
          this.stickBase.classList.add('tc-visible');
        } else if (this.lookTouch === null) {
          this.lookTouch = t.identifier;
          this.lookLast = { x: t.clientX, y: t.clientY };
        }
      }
    };

    const onMove = (e: TouchEvent) => {
      if (!this.visible) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.stickTouch) {
          e.preventDefault();
          let dx = t.clientX - this.stickOrigin.x;
          let dy = t.clientY - this.stickOrigin.y;
          const len = Math.hypot(dx, dy);
          if (len > this.stickRadius) {
            dx = (dx / len) * this.stickRadius;
            dy = (dy / len) * this.stickRadius;
          }
          this.stickKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
          // Ekranda aşağı = ileri değil: y ters çevrilir
          this.input.setStick(dx / this.stickRadius, -dy / this.stickRadius);
        } else if (t.identifier === this.lookTouch) {
          const dx = t.clientX - this.lookLast.x;
          const dy = t.clientY - this.lookLast.y;
          this.lookLast = { x: t.clientX, y: t.clientY };
          this.input.addLook(-dx * 0.005, -dy * 0.005, true);
        }
      }
    };

    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        const action = this.buttonTouches.get(t.identifier);
        if (action) {
          this.buttonTouches.delete(t.identifier);
          this.input.setTouchAction(action, false);
          for (const el of Array.from(this.root.querySelectorAll('.tc-btn.tc-active'))) {
            if ((el as HTMLElement).dataset.action === action) el.classList.remove('tc-active');
          }
          continue;
        }
        if (t.identifier === this.stickTouch) {
          this.stickTouch = null;
          this.input.setStick(0, 0);
          this.stickKnob.style.transform = 'translate(-50%, -50%)';
          this.stickBase.classList.remove('tc-visible');
        } else if (t.identifier === this.lookTouch) {
          this.lookTouch = null;
        }
      }
    };

    // passive:false -> preventDefault ile sayfa kaydırması engellenir
    window.addEventListener('touchstart', onStart, { passive: false });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
  }
}
